import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';

import { getMyPolicyTask, openPolicyTask, startPolicyQuiz, submitPolicyQuiz } from '../../services/policyService';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 28px;
`;

const StepDot = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  background: ${({ $active, $completed, theme }) =>
    $active ? theme.colors.primary.main :
    $completed ? '#16a34a' : '#E0E4E8'};
  color: ${({ $active, $completed }) => ($active || $completed) ? '#fff' : '#64748b'};
`;

const StepLine = styled.div`
  flex: 1;
  height: 2px;
  background: ${({ $completed }) => $completed ? '#16a34a' : '#E0E4E8'};
`;

const StepLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $active, theme }) => $active ? theme.colors.text.primary : '#A0A8B4'};
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const PdfFrame = styled.iframe`
  width: 100%;
  height: 500px;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

const QuestionCard = styled(Card)`
  border-left: 4px solid ${({ theme }) => theme.colors.primary.main};
`;

const QuestionText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #004242;
  margin-bottom: 16px;
`;

const OptionLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #004242;
  margin-bottom: 8px;
  background: ${({ $selected }) => $selected ? '#0291EF10' : '#F5F7FA'};
  border: 2px solid ${({ $selected }) => $selected ? '#0291EF' : 'transparent'};
  transition: all 0.15s ease;
  &:hover { background: #0291EF08; }
`;

const ResultCard = styled(Card)`
  text-align: center;
  padding: 48px;
`;

const ResultIcon = styled.div`
  margin-bottom: 16px;
`;

const ResultTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: ${({ $passed }) => $passed ? '#16a34a' : '#ef4444'};
  margin-bottom: 8px;
`;

const ResultScore = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #004242;
  margin-bottom: 24px;
`;

export default function PolicyTaskViewPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();

  const [task, setTask] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Quiz state
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [attemptId, setAttemptId] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    getMyPolicyTask(id)
      .then((res) => {
        const data = res.data?.data;
        setTask(data);
        if (data?.status === 'completed') {
          setStep(3);
          setResult({ passed: true, score: data.score || 100 });
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleUnderstood = useCallback(async () => {
    try {
      await openPolicyTask(id);
      // Check if quiz exists
      if (task?.hasQuiz) {
        setSubmitting(true);
        const res = await startPolicyQuiz(id);
        const quizData = res.data?.data;
        if (quizData && quizData.questions) {
          setQuestions(quizData.questions);
          setAttemptId(quizData.attemptId);
          setAnswers({});
          setStep(2);
        } else {
          setStep(3);
          setResult({ passed: true, score: 100 });
        }
        setSubmitting(false);
      } else {
        setStep(3);
        setResult({ passed: true, score: 100 });
      }
    } catch {
      setSubmitting(false);
    }
  }, [id, task]);

  const handleSelectAnswer = useCallback((questionId, optionId) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  }, []);

  const handleSubmitQuiz = useCallback(async () => {
    setSubmitting(true);
    try {
      const payload = {
        attemptId,
        answers: Object.entries(answers).map(([questionId, selectedOptionId]) => ({
          questionId,
          selectedOptionId,
        })),
      };
      const res = await submitPolicyQuiz(id, payload);
      const data = res.data?.data;
      setResult({
        passed: data?.passed ?? false,
        score: data?.score ?? 0,
        maxScore: data?.maxScore ?? 0,
      });
      setStep(3);
    } catch {
      // error handled by interceptor
    } finally {
      setSubmitting(false);
    }
  }, [id, answers, attemptId]);

  const handleRetry = useCallback(async () => {
    setSubmitting(true);
    try {
      const res = await startPolicyQuiz(id);
      const quizData = res.data?.data;
      if (quizData && quizData.questions) {
        setQuestions(quizData.questions);
        setAttemptId(quizData.attemptId);
        setAnswers({});
        setResult(null);
        setStep(2);
      }
    } catch {
      // error handled by interceptor
    } finally {
      setSubmitting(false);
    }
  }, [id]);

  if (loading) return null;

  const docTitle = task?.documentTitle || task?.title || '...';
  const hasQuiz = task?.hasQuiz;
  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id] !== undefined);

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('policy.myTasks'), path: '/my-policy-tasks' },
          { label: docTitle },
        ]} />

        <StepIndicator>
          <StepDot $active={step === 1} $completed={step > 1}>1</StepDot>
          <StepLabel $active={step === 1}>{t('policy.stepRead')}</StepLabel>
          <StepLine $completed={step > 1} />
          <StepDot $active={step === 2} $completed={step > 2}>2</StepDot>
          <StepLabel $active={step === 2}>{t('policy.stepQuiz')}</StepLabel>
          <StepLine $completed={step > 2} />
          <StepDot $active={step === 3}>3</StepDot>
          <StepLabel $active={step === 3}>{t('policy.stepResult')}</StepLabel>
        </StepIndicator>
      </StickyHeader>

      <Content>
        {/* Step 1 - Read document */}
        {step === 1 && (
          <Card>
            {task?.fileUrl && (
              <PdfFrame src={task.fileUrl} title={docTitle} />
            )}
            <Footer>
              <Button onClick={handleUnderstood} disabled={submitting}>
                {submitting ? '...' : t('policy.understood')}
              </Button>
            </Footer>
          </Card>
        )}

        {/* Step 2 - Quiz */}
        {step === 2 && (
          <>
            {questions.map((q, idx) => (
              <QuestionCard key={q.id}>
                <QuestionText>{idx + 1}. {q.questionText || q.text}</QuestionText>
                {(q.options || []).map((opt) => (
                  <OptionLabel
                    key={opt.id}
                    $selected={answers[q.id] === opt.id}
                    onClick={() => handleSelectAnswer(q.id, opt.id)}
                  >
                    <input
                      type="radio"
                      name={`question-${q.id}`}
                      checked={answers[q.id] === opt.id}
                      onChange={() => handleSelectAnswer(q.id, opt.id)}
                      style={{ accentColor: '#0291EF' }}
                    />
                    {opt.optionText || opt.text || opt.label}
                  </OptionLabel>
                ))}
              </QuestionCard>
            ))}
            <Footer>
              <Button onClick={handleSubmitQuiz} disabled={!allAnswered || submitting}>
                {submitting ? '...' : t('policy.submitQuiz')}
              </Button>
            </Footer>
          </>
        )}

        {/* Step 3 - Result */}
        {step === 3 && result && (
          <ResultCard>
            <ResultIcon>
              {result.passed
                ? <CheckCircle size={64} color="#16a34a" />
                : <XCircle size={64} color="#ef4444" />
              }
            </ResultIcon>
            <ResultTitle $passed={result.passed}>
              {result.passed ? t('policy.quizPassed') : t('policy.quizFailed')}
            </ResultTitle>
            <ResultScore>
              {t('policy.yourScore', {
                correct: result.score,
                total: result.maxScore,
                pct: result.maxScore > 0 ? Math.round((result.score / result.maxScore) * 100) : 0,
              })}
            </ResultScore>
            {!result.passed && hasQuiz && (
              <Button variant="outline" onClick={handleRetry} disabled={submitting}>
                {t('policy.retry')}
              </Button>
            )}
            <div style={{ marginTop: 16 }}>
              <Button variant="ghost" onClick={() => history.push('/my-policy-tasks')}>
                {t('policy.backToTasks')}
              </Button>
            </div>
          </ResultCard>
        )}
      </Content>
    </PageContainer>
  );
}
